import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LeaveCarryOverPreview } from '@mnc/contracts';
import { api } from '../../lib/api';
import { fmtDate, useHours } from './shared';

/**
 * What renewing a contract does to its annual leave, shown in the renewal window: the balance on the contract's last
 * day and what the renewal carries, forfeits above the cap, or writes off. The server's own rule, not a guess here.
 */
export function CarryOverPreview({ contractId }: { contractId: string }) {
  const { t, i18n } = useTranslation();
  const { both } = useHours();
  const q = useQuery({ queryKey: ['leave', 'carry-over', contractId], queryFn: () => api.get<LeaveCarryOverPreview>(`/api/leave/contracts/${contractId}/carry-over-preview`), retry: false });
  const d = q.data;
  if (!d) return null;
  const line = (label: string, hours: number, sign = '', strong = false) => (
    <>
      <dt className={strong ? 'font-semibold' : 'text-neutral-600 dark:text-neutral-300'}>{label}</dt>
      <dd className={`text-end tabular-nums ${strong ? 'font-semibold' : ''}`} dir="ltr">{sign}{both(Math.abs(hours), d.hours_per_day)}</dd>
    </>
  );
  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50 p-3 text-sm dark:border-brand-900 dark:bg-brand-950/40">
      <div className="mb-2 font-medium">{t('leave.carry.title')}</div>
      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1">
        {line(t('leave.carry.closing', { date: fmtDate(d.closing_date, i18n.language) }), d.closing_hours, d.closing_hours < 0 ? '−' : '')}
        {d.forfeited_hours > 0 && line(t('leave.carry.forfeited', { cap: d.cap_days }), d.forfeited_hours, '−')}
        {d.written_off_hours > 0 && line(t('leave.carry.writtenOff'), d.written_off_hours, '+')}
        {line(t('leave.carry.carried'), d.carried_hours, d.carried_hours < 0 ? '−' : '', true)}
      </dl>
      {d.pending_hours > 0 && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{t('leave.carry.pending', { value: both(d.pending_hours, d.hours_per_day) })}</p>}
      <p className="mt-2 text-xs text-neutral-500">{t('leave.carry.terms', { days: d.annual_days })}</p>
    </div>
  );
}
